import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUp02Icon,
  ArrowUpDoubleIcon,
  DragDropVerticalIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps
} from "@hello-pangea/dnd";
import { forwardRef } from "react";
import { cx } from "shared/utils";
import {
  ContextMenuPopover,
  Action
} from "shared/components/ContextMenuPopover";
import { Avatar } from "shared/components/Avatar";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { Card } from "shared/components/Card";
import { RotationUser } from "./types";

interface RotationUserCardProps {
  user: RotationUser;
  index: number;
  totalUsers: number;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
  onSetNext: (userId: number) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
}

export const RotationUserCard = forwardRef<
  HTMLDivElement,
  RotationUserCardProps
>(
  (
    {
      user,
      index,
      totalUsers,
      draggableProps,
      dragHandleProps,
      isDragging,
      onSetNext,
      onMoveUp,
      onMoveDown
    },
    ref
  ) => {
    const { style, ...restDraggableProps } = draggableProps ?? {};

    const isFirst = index === 0;
    const isLast = index === totalUsers - 1;

    const menuItems: {
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled: boolean;
    }[] = [
      {
        label: "Set next",
        icon: <HugeiconsIcon icon={ArrowUpDoubleIcon} size={20} />,
        onClick: () => onSetNext(user.id),
        disabled: user.is_next
      },
      {
        label: "Move up",
        icon: <HugeiconsIcon icon={ArrowUp02Icon} size={20} />,
        onClick: () => onMoveUp?.(index),
        disabled: isFirst
      },
      {
        label: "Move down",
        icon: (
          <HugeiconsIcon
            icon={ArrowUp02Icon}
            size={20}
            className="rotate-180"
          />
        ),
        onClick: () => onMoveDown?.(index),
        disabled: isLast
      }
    ];

    const actions: Action[] = menuItems.map((item) => ({
      title: (
        <span
          className={cx(
            "flex items-center gap-3 text-base font-medium",
            item.disabled ? "text-content-300" : "text-content-1000"
          )}
        >
          {item.icon}
          {item.label}
        </span>
      ),
      onClick: item.onClick,
      disabled: item.disabled
    }));

    return (
      <div
        ref={ref}
        style={{
          ...style,
          transform: isDragging
            ? `${style?.transform ?? ""} rotate(2deg)`
            : style?.transform
        }}
        {...restDraggableProps}
      >
        <Card
          className={cx(
            "flex items-center gap-3 md:gap-4 p-3 md:p-4",
            isDragging && "shadow-lg"
          )}
        >
          <div
            className="shrink-0 cursor-grab active:cursor-grabbing pr-3 md:pr-4 border-r border-alpha-40"
            {...dragHandleProps}
          >
            <HugeiconsIcon
              icon={DragDropVerticalIcon}
              size={24}
              strokeWidth={3}
              className="text-content-1000"
            />
          </div>

          <Avatar name={user.name} size={40} />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-content-1000 truncate">
                {user.name}
              </span>

              <Show if={user.is_next}>
                <Badge variant={BadgeVariant.PRIMARY} size={BadgeSize.SM}>
                  Next
                </Badge>
              </Show>
            </div>
            <p className="mt-2 text-sm text-content-300">
              {user.showings_today} showings today &middot;{" "}
              {user.showings_this_week} showings this week
            </p>
          </div>

          <div className="shrink-0">
            <ContextMenuPopover actions={actions} borderLess={false} />
          </div>
        </Card>
      </div>
    );
  }
);

RotationUserCard.displayName = "RotationUserCard";
